import { type FC, useState } from "react"
import { useNavigate } from "react-router"

import { toDateString } from "@forento/core/utilities/date"
import Tooltip from "@forento/ui/components/Tooltip"

import Button, { SubmitButton } from "~/components/Button"
import Dropdown from "~/components/Dropdown"
import { ArrowIcon, DeleteIcon } from "~/components/Icon"
import Layout, { PageTitle } from "~/components/Layout"
import { DefaultModal, ModalButtons, ModalInputs, ModalText, ModalTitle } from "~/components/Modal"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import Table from "~/components/Table"
import { useAlert } from "~/contexts/AlertContext"
import { useUser } from "~/contexts/UserContext"
import routes from "~/utilities/routes"
import trpc, { query } from "~/utilities/trpc"

const ExamsPage: FC = () => {
	const alert = useAlert()

	const exams = query.exam.list.useQuery()
	const courses = query.course.list.useQuery()

	const handleDelete = async (id: number) => {
		const dialog = await alert.confirm(
			"Delete exam",
			"Are you sure you want to delete this exam? This action cannot be undone.",
		)
		if (!dialog.result) return

		try {
			await trpc.exam.delete.mutate(id)
			await exams.refetch()
			dialog.close()
		} catch (error) {
			console.error(error)
			await alert.show("Error", "Failed to delete exam. Please try again later.")
		}
	}

	return (
		<Layout>
			<PageTitle>Exams</PageTitle>
			{exams.error ? (
				<p>Failed to load exams.</p>
			) : exams.data === undefined || courses.data === undefined ? (
				<PartialLoadingPage />
			) : (
				<>
					<p className="mb-4 rounded-lg bg-amber-200 px-5 py-3">
						Exams have moved to courses! All exams are now connected to a course and act as an examination
						after a completed course. You can migrate your existing exams to courses below.
					</p>
					<Table
						columns={["Create date", "Name", "Actions"]}
						data={[...exams.data].map(exam => [
							toDateString(exam.createDate),
							exam.name,
							<div className="flex items-center gap-4">
								<Tooltip tooltip="Delete">
									<Button onClick={() => handleDelete(exam.id)} className="h-6 w-6">
										<DeleteIcon className="text-danger h-full w-full" />
									</Button>
								</Tooltip>
								<MigrateToCourse exam={exam} reload={exams.refetch} />
							</div>,
						])}
					/>
				</>
			)}
		</Layout>
	)
}

type MigrateToCourseProps = { exam: { id: number; name: string }; reload(): Promise<unknown> }
const MigrateToCourse: FC<MigrateToCourseProps> = ({ exam, reload }) => {
	const user = useUser()
	const navigate = useNavigate()
	const alert = useAlert()

	const [isModalOpen, setModalOpen] = useState(false)
	const [courseId, setCourseId] = useState<string>("NONE")

	const courses = query.course.list.useQuery()

	const migrate = query.exam.migrateToCourse.useMutation({
		async onSuccess(_, variables) {
			await Promise.all([user.reload(), reload(), courses.refetch()])

			navigate(routes.course.exam(variables.courseId))
		},
		async onError(error) {
			console.error(error)
			await alert.show("Error", "Failed to migrate exam. Please try again later.")
		},
	})

	return (
		<>
			<DefaultModal
				onClose={() => setModalOpen(false)}
				isOpen={isModalOpen}
				onSubmit={() => migrate.mutate({ examId: exam.id, courseId: parseInt(courseId) })}
			>
				<ModalTitle>Migrate exam</ModalTitle>
				<ModalText>
					You are about to migrate the exam <strong>{exam.name}</strong>. Please choose a course to attach it
					to.
				</ModalText>
				{courses.error ? (
					<ModalText>Failed to load courses.</ModalText>
				) : courses.data === undefined ? (
					<PartialLoadingPage />
				) : (
					<ModalInputs>
						<Dropdown
							items={[
								{ id: "NONE", title: "-- Select course --" },
								...courses.data
									.filter(x => !x.hasExam)
									.map(x => ({ id: x.id.toString(), title: x.title })),
							]}
							selectedItemId={courseId ?? "NONE"}
							onChange={setCourseId}
						/>
					</ModalInputs>
				)}
				<ModalButtons>
					<Button variant="secondary" onClick={() => setModalOpen(false)} isDisabled={migrate.isPending}>
						Cancel
					</Button>
					<SubmitButton variant="primary" isDisabled={courseId === "NONE"} isLoading={migrate.isPending}>
						Migrate
					</SubmitButton>
				</ModalButtons>
			</DefaultModal>
			<Tooltip tooltip="Migrate to course">
				<Button onClick={() => setModalOpen(true)} className="h-6 w-6">
					<ArrowIcon direction="right" className="h-full w-full" />
				</Button>
			</Tooltip>
		</>
	)
}

export default ExamsPage
